<template lang="pug">
.organizations-block(v-if="organizationsBlock.organizations.length > 0")
  // Section title
  .section-title(v-if="organizationsBlock.title")
    h6.mb-0 {{ organizationsBlock.title.toUpperCase() }}

  // Organizations grid - fixed 3 columns
  .organizations-grid
    .organization-card(
      v-for="(organization, index) in organizationsBlock.organizations"
      :key="organization.id || index"
      @click="handleOrganizationClick(organization)"
    )
      // Organization avatar
      .organization-avatar-container
        img(
          :src="organization.avatar || orgPlaceholder"
          :alt="organization.name"
          @error="handleImageError"
        ).organization-avatar

      // Organization name
      .organization-name
        span {{ organization.name }}
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { useRouter } from 'vue-router';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { OrganizationsBlock as OrganizationsBlockType } from '../types';

export default defineComponent({
  name: 'OrganizationsBlock',
  props: {
    block: {
      type: Object as PropType<OrganizationsBlockType>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();

    // Organization placeholder with building emoji
    const orgPlaceholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23e8f5f4" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" font-size="40" fill="%231fbeaf"%3E🏢%3C/text%3E%3C/svg%3E';

    // Computed
    const organizationsBlock = computed(() => props.block as OrganizationsBlockType);

    // Methods
    const handleOrganizationClick = (organization: any) => {
      if (!organization.id) return;

      // Navigate to organization public profile
      // Use slug if available, otherwise use id
      if (organization.slug) {
        router.push(`/web/org/${organization.slug}`);
      } else {
        router.push(`/web/profile/organization/${organization.id}`);
      }
    };

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      // Prevent infinite loop
      if (target.src.startsWith('data:image/svg')) return;
      // Fallback to organization placeholder
      target.src = orgPlaceholder;
    };

    return {
      organizationsBlock,
      orgPlaceholder,
      handleOrganizationClick,
      handleImageError,
    };
  },
});
</script>

<style lang="scss" scoped>
.organizations-block {
  margin-bottom: 48px;
  padding: 0 24px;

  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 56px;
  }

  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 64px;
  }
}

.section-title {
  margin-bottom: 24px;

  h6 {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #1a1a1a;
    margin: 0;

    @media (min-width: 768px) {
      font-size: 15px;
    }
  }
}

.organizations-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 24px;
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(6, 1fr);
  }
}

.organization-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);

    .organization-avatar-container {
      box-shadow: 0 8px 24px rgba(13, 148, 136, 0.15);
    }

    .organization-name span {
      color: #0d9488;
    }
  }
}

.organization-avatar-container {
  margin-bottom: 8px;
  aspect-ratio: 1;
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.organization-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .organization-card:hover & {
    transform: scale(1.05);
  }
}

.organization-name {
  padding: 0 4px;

  span {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: #1a1a1a;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
    transition: color 0.2s;

    @media (min-width: 768px) {
      font-size: 14px;
    }
  }
}
</style>
